import React, { useState, useEffect } from 'react';
import { Save, X, Upload, Loader2, Calculator, Package, FileText, Percent, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { orderService } from '../../services/orderService';
import { quoteService } from '../../services/quoteService';
import { productService } from '../../services/productService';
import { clientService } from '../../services/clientService';
import { notificationService } from '../../services/notificationService';
import { OrderWithDetails, OrderFormData, OrderItem } from '../../types/order';
import { Product } from '../../types/product';
import { Customer } from '../../types/customer';
import { QuoteWithDetails } from '../../services/quoteService';

// Exportar para que sea considerado un módulo
export {};

interface OrderFormProps {
  order?: OrderWithDetails | null;
  onSave: () => void;
  onCancel: () => void;
}

const OrderForm = ({ order, onSave, onCancel }: OrderFormProps) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [quotes, setQuotes] = useState<QuoteWithDetails[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showCustomerResults, setShowCustomerResults] = useState(false);
  const [quoteSearchTerm, setQuoteSearchTerm] = useState('');
  const [showQuoteResults, setShowQuoteResults] = useState(false);
  
  const [formData, setFormData] = useState<OrderFormData>({
    order_number: '',
    customer_name: '',
    customer_id: undefined,
    quote_id: undefined,
    items: [],
    currency: 'USD',
    payment_terms: 'Contado',
    payment_type: 'contado',
    credit_days: 0,
    delivery_days: 5,
    delivery_time: '',
    notes: '',
    calculate_detraccion: false,
    detraccion_percentage: 12,
    order_document: undefined,
    order_date: new Date().toLocaleDateString('en-CA')
  });

  const [calculatedAmounts, setCalculatedAmounts] = useState({
    subtotal: 0,
    igvAmount: 0,
    totalWithIgv: 0,
    detraccionAmount: 0
  });

  useEffect(() => {
    loadProducts();
    loadQuotes();
    loadCustomers();
  }, []);

  useEffect(() => {
    if (customerSearchTerm.length > 2) {
      searchCustomers();
    } else {
      setShowCustomerResults(false);
    }
  }, [customerSearchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (order) {
      // Convertir la orden existente al nuevo formato
      const items: OrderItem[] = [{
        product_id: order.product_id,
        product_name: order.product?.name || '',
        quantity: order.quantity,
        unit_price: order.unit_price,
        subtotal: order.subtotal
      }];

      setFormData({
        order_number: order.order_number,
        customer_name: order.customer_name,
        customer_id: order.customer_id,
        quote_id: order.quote_id,
        items: items,
        currency: order.currency,
        payment_terms: order.payment_terms,
        payment_type: order.payment_type || 'contado',
        credit_days: order.credit_days || 0,
        delivery_days: order.delivery_days || 5,
        delivery_time: order.delivery_time,
        notes: order.notes,
        calculate_detraccion: order.calculate_detraccion,
        detraccion_percentage: order.detraccion_percentage,
        order_document: undefined,
        order_date: order.order_date
      });
    }
  }, [order]);

  useEffect(() => {
    // Calcular montos automáticamente cuando cambian los valores
    const subtotal = formData.items.reduce((sum, item) => sum + item.subtotal, 0);
    const igvAmount = subtotal * 0.18; // Siempre calcular IGV (18%)
    const totalWithIgv = subtotal + igvAmount;
    const detraccionAmount = formData.calculate_detraccion ? totalWithIgv * (formData.detraccion_percentage / 100) : 0;
    
    setCalculatedAmounts({
      subtotal,
      igvAmount,
      totalWithIgv,
      detraccionAmount
    });
  }, [formData.items, formData.calculate_detraccion, formData.detraccion_percentage]);

  const loadProducts = async () => {
    try {
      const data = await productService.getProducts();
      setProducts(data);
    } catch (error) {
      console.error('Error al cargar productos:', error);
      notificationService.error('Error al cargar productos', 'No se pudieron cargar los productos. Por favor, recargue la página.');
    }
  };

  const loadQuotes = async () => {
    try {
      const data = await quoteService.getQuotes();
      setQuotes(data);
    } catch (error) {
      console.error('Error al cargar cotizaciones:', error);
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await clientService.getCustomers();
      setCustomers(data);
    } catch (error) {
      console.error('Error al cargar clientes:', error);
    }
  };

  const searchCustomers = async () => {
    try {
      const data = await clientService.searchCustomers(customerSearchTerm);
      setCustomers(data);
      setShowCustomerResults(true);
    } catch (error) {
      console.error('Error al buscar clientes:', error);
    }
  };

  const handleQuoteSelection = async (quoteId: string) => {
    if (!quoteId || quoteId === '0') {
      // Limpiar datos si no se selecciona cotización
      setFormData(prev => ({
        ...prev,
        quote_id: undefined,
        customer_name: '',
        customer_id: undefined,
        items: []
      }));
      return;
    }

    setIsLoadingQuote(true);
    try {
      // Primero obtener la cotización completa con sus detalles
      const quoteWithDetails = await quoteService.getQuoteWithDetails(parseInt(quoteId));
      
      if (quoteWithDetails) {
        // Construir el nombre del cliente con empresa
        let customerDisplayName = '';
        if (quoteWithDetails.company) {
          customerDisplayName = quoteWithDetails.company;
        } else {
          customerDisplayName = quoteWithDetails.name || '';
        }

        // Crear items a partir de los detalles de la cotización
        const items: OrderItem[] = [];
        
        if (quoteWithDetails.quote_details && quoteWithDetails.quote_details.length > 0) {
          // Si hay detalles, usarlos
          quoteWithDetails.quote_details.forEach(detail => {
            const selectedProduct = products.find(p => p.id === detail.product_id);
            items.push({
              product_id: detail.product_id,
              product_name: selectedProduct?.name || '',
              quantity: detail.quantity,
              unit_price: detail.unit_price,
              subtotal: detail.quantity * detail.unit_price
            });
          });
        } else if (quoteWithDetails.product_id) {
          // Si no hay detalles pero hay un producto principal, usarlo
          const selectedProduct = products.find(p => p.id === quoteWithDetails.product_id);
          items.push({
            product_id: quoteWithDetails.product_id,
            product_name: selectedProduct?.name || quoteWithDetails.product_name || '',
            quantity: 1, // Valor por defecto si no hay detalles
            unit_price: 0, // Se cargará desde el producto
            subtotal: 0
          });
        }

        // Extraer moneda de la cotización si existe
        const quoteCurrency = quoteWithDetails.currency || 'USD';

        // Determinar el tipo de pago y días de crédito a partir de los términos de pago
        let paymentType: 'contado' | 'credito' = 'contado';
        let creditDays = 0;
        let paymentTerms = quoteWithDetails.payment_terms || '';

        // Analizar los términos de pago para determinar el tipo y días de crédito
        if (paymentTerms.toLowerCase().includes('crédito') || paymentTerms.toLowerCase().includes('credito')) {
          paymentType = 'credito';
          // Extraer el número de días del texto "Crédito a X días"
          const creditDaysMatch = paymentTerms.match(/(\d+)\s*días/);
          if (creditDaysMatch) {
            creditDays = parseInt(creditDaysMatch[1]);
          } else {
            creditDays = 30; // Valor por defecto
          }
        } else {
          paymentType = 'contado';
          creditDays = 0;
          paymentTerms = 'Contado';
        }

        setFormData(prev => ({
          ...prev,
          quote_id: quoteWithDetails.id,
          customer_name: customerDisplayName,
          customer_id: quoteWithDetails.customers?.id || quoteWithDetails.customer_id,
          items: items,
          currency: quoteCurrency,
          payment_terms: paymentTerms,
          payment_type: paymentType,
          credit_days: creditDays,
          delivery_days: quoteWithDetails.delivery_days || 5,
          delivery_time: quoteWithDetails.delivery_terms || `${quoteWithDetails.delivery_days || 5} días hábiles después de la orden de compra`
        }));

        // También actualizar el término de búsqueda del cliente
        setCustomerSearchTerm(customerDisplayName);
      }
    } catch (error) {
      console.error('Error al cargar datos de cotización:', error);
      notificationService.error('Error', 'No se pudieron cargar los datos de la cotización seleccionada.');
    } finally {
      setIsLoadingQuote(false);
    }
  };

  const addOrderItem = () => {
    const newItem: OrderItem = {
      product_id: 0,
      product_name: '',
      quantity: 1,
      unit_price: 0,
      subtotal: 0
    };
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  const updateOrderItem = (index: number, field: keyof OrderItem, value: any) => {
    const updatedItems = [...formData.items];
    const item = updatedItems[index];
    
    if (field === 'product_id') {
      const selectedProduct = products.find(p => p.id === value);
      if (selectedProduct) {
        item.product_id = value;
        item.product_name = selectedProduct.name;
        // Cargar precio unitario automáticamente siempre al seleccionar producto
        item.unit_price = selectedProduct.price || 0;
        item.subtotal = item.quantity * item.unit_price;
      }
    } else if (field === 'quantity') {
      item.quantity = value;
      item.subtotal = item.quantity * item.unit_price;
    } else if (field === 'unit_price') {
      item.unit_price = value;
      item.subtotal = item.quantity * item.unit_price;
    } else if (field === 'product_name') {
      item.product_name = value;
    }

    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
  };

  const removeOrderItem = (index: number) => {
    const updatedItems = formData.items.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.customer_name.trim()) {
      newErrors.customer_name = 'El nombre del cliente es obligatorio';
    }

    if (formData.items.length === 0) {
      newErrors.items = 'Debe agregar al menos un producto';
    } else {
      formData.items.forEach((item, index) => {
        if (item.product_id === 0) {
          newErrors[`product_id_${index}`] = 'Debe seleccionar un producto';
        }
        if (item.quantity <= 0) {
          newErrors[`quantity_${index}`] = 'La cantidad debe ser mayor a 0';
        }
        if (item.unit_price <= 0) {
          newErrors[`unit_price_${index}`] = 'El precio unitario debe ser mayor a 0';
        }
      });
    }

    if (!formData.currency) {
      newErrors.currency = 'Debe seleccionar una moneda';
    }

    if (!formData.payment_terms.trim()) {
      newErrors.payment_terms = 'El término de pago es obligatorio';
    }

    if (!formData.delivery_days || formData.delivery_days <= 0) {
      newErrors.delivery_days = 'El tiempo de entrega es obligatorio';
    }

    if (!formData.order_date) {
      newErrors.order_date = 'La fecha de la orden es obligatoria';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      let success = false;
      if (order) {
        // Editar orden existente
        const updatedOrder = await orderService.updateOrder(order.id, formData);
        success = updatedOrder !== null;
      } else {
        // Agregar nueva orden
        const newOrder = await orderService.addOrder(formData);
        success = newOrder !== null;
      }

      if (success) {
        notificationService.success(
          'Orden guardada exitosamente', 
          order ? 'La orden ha sido actualizada correctamente.' : 'La orden ha sido registrada correctamente.'
        );
        onSave();
      } else {
        notificationService.error('Error al guardar orden', 'Por favor, verifique los datos e intente nuevamente.');
      }
    } catch (error) {
      console.error('Error al guardar orden:', error);
      notificationService.error('Error al guardar orden', 'Ha ocurrido un error inesperado. Por favor, intente nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDocumentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    
    // Validar tipo de archivo
    if (!file.type.startsWith('image/') && !file.type.includes('pdf')) {
      notificationService.warning('Archivo no válido', 'Por favor, seleccione un archivo de imagen o PDF válido.');
      return;
    }

    // Validar tamaño (máximo 10MB)
    if (file.size > 10 * 1024 * 1024) {
      notificationService.warning('Archivo demasiado grande', 'El documento no debe superar los 10MB.');
      return;
    }

    setIsUploading(true);
    try {
      // Solo guardamos el archivo en el estado, no lo subimos todavía
      // La subida se hará cuando se guarde la orden para evitar duplicación
      setFormData(prev => ({
        ...prev,
        order_document: file
      }));

      notificationService.success('Documento seleccionado', 'El documento se subirá cuando guarde la orden.');
    } catch (error) {
      console.error('Error al procesar el documento:', error);
      notificationService.error('Error al procesar documento', 'Ha ocurrido un error al procesar el documento. Por favor, intente nuevamente.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : 
             type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));

    // Limpiar error cuando el usuario empieza a escribir
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: formData.currency
    }).format(amount);
  };

  const getCurrencySymbol = () => {
    switch (formData.currency) {
      case 'PEN':
        return 'S/';
      case 'USD':
        return '$';
      default:
        return '';
    }
  };

  const handleCustomerSelect = (customer: Customer) => {
    // Construir el nombre del cliente mostrando empresa y RUC
    let displayName = '';
    if (customer.company) {
      displayName = customer.company;
      if (customer.ruc) {
        displayName += ` (RUC: ${customer.ruc})`;
      }
    } else {
      displayName = customer.name || '';
    }
    
    setFormData(prev => ({
      ...prev,
      customer_name: displayName,
      customer_id: customer.id
    }));
    setShowCustomerResults(false);
    setCustomerSearchTerm(displayName);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-3 sm:p-4 lg:p-6">
      <div className="flex justify-between items-center mb-4 sm:mb-6">
        <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 truncate pr-2">
          {order ? 'Editar Orden de Pedido' : 'Registrar Nueva Orden de Pedido'}
        </h3>
        <Button variant="outline" size="sm" onClick={onCancel} className="flex-shrink-0">
          <X className="w-4 h-4" />
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        {/* Selección de cotización como referencia */}
        <div className="relative">
          <label htmlFor="quote_search" className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
            Cotización de Referencia (Opcional)
          </label>
          <div className="relative">
            <input
              type="text"
              id="quote_search"
              value={quoteSearchTerm}
              onChange={(e) => {
                setQuoteSearchTerm(e.target.value);
                // Filtrar cotizaciones basadas en el término de búsqueda
                if (e.target.value.length > 0) {
                  setShowQuoteResults(true);
                } else {
                  setShowQuoteResults(false);
                }
              }}
              onFocus={() => {
                setShowQuoteResults(true);
                // Cargar todas las cotizaciones si no hay datos
                if (quotes.length === 0) {
                  loadQuotes();
                }
              }}
              onBlur={() => {
                // Ocultar resultados después de un pequeño retraso para permitir clics
                setTimeout(() => setShowQuoteResults(false), 200);
              }}
              className="w-full px-3 sm:px-4 py-2 sm:py-2.5 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base"
              placeholder="Buscar por código de cotización..."
            />
            <Search className="absolute right-3 top-2.5 sm:top-3 w-4 h-4 text-gray-400" />
          </div>
          
          {/* Resultados de búsqueda de cotizaciones */}
          {showQuoteResults && (
            <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg shadow-lg mt-1 max-h-60 overflow-y-auto overflow-x-hidden">
              {quotes
                .filter(quote =>
                  quoteSearchTerm === '' ||
                  (quote.id && quote.id.toString().includes(quoteSearchTerm)) ||
                  (quote.message && quote.message.toLowerCase().includes(quoteSearchTerm.toLowerCase()))
                )
                .map(quote => (
                  <div
                    key={quote.id || 0}
                    className="px-3 sm:px-4 py-2 sm:py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                    onClick={() => {
                      // Extraer el código de cotización del mensaje si existe
                      const quoteCodeMatch = quote.message?.match(/QT-\d{8}-\d{3}/);
                      const quoteCode = quoteCodeMatch ? quoteCodeMatch[0] : `COT-${quote.id || 0}`;
                      setQuoteSearchTerm(quoteCode);
                      handleQuoteSelection((quote.id || 0).toString());
                      setShowQuoteResults(false);
                    }}
                  >
                    <div className="font-medium text-gray-900 text-sm sm:text-base">
                      Código: {(() => {
                        const quoteCodeMatch = quote.message?.match(/QT-\d{8}-\d{3}/);
                        return quoteCodeMatch ? quoteCodeMatch[0] : `COT-${quote.id || 0}`;
                      })()}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600">ID: #{quote.id || 0}</div>
                    <div className="text-xs sm:text-sm text-gray-600">Cliente: {quote.company || quote.name}</div>
                    {quote.created_at && (
                      <div className="text-xs text-gray-500">
                        Fecha: {new Date(quote.created_at).toLocaleDateString('es-PE')}
                      </div>
                    )}
                  </div>
                ))}
              {quotes.filter(quote =>
                quoteSearchTerm === '' ||
                (quote.id && quote.id.toString().includes(quoteSearchTerm)) ||
                (quote.message && quote.message.toLowerCase().includes(quoteSearchTerm.toLowerCase()))
              ).length === 0 && (
                <div className="px-3 sm:px-4 py-2 sm:py-3 text-gray-500 text-sm">
                  No se encontraron cotizaciones
                </div>
              )}
            </div>
          )}
          
          {isLoadingQuote && (
            <div className="mt-2 flex items-center text-xs sm:text-sm text-gray-600">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Cargando datos de cotización...
            </div>
          )}
        </div>

        {/* Información del cliente */}
        <div className="relative">
          <label htmlFor="customer_name" className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
            Nombre del Cliente / Empresa *
          </label>
          <div className="relative">
            <input
              type="text"
              id="customer_name"
              name="customer_name"
              value={customerSearchTerm || formData.customer_name}
              onChange={(e) => {
                setCustomerSearchTerm(e.target.value);
                handleInputChange(e);
              }}
              onFocus={() => {
                setShowCustomerResults(true);
                // Cargar todos los clientes cuando se enfoca el campo
                if (customers.length === 0) {
                  loadCustomers();
                }
              }}
              onBlur={() => {
                // Ocultar resultados después de un pequeño retraso para permitir clics
                setTimeout(() => setShowCustomerResults(false), 200);
              }}
              className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 pr-10 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base ${
                errors.customer_name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Buscar cliente por nombre o empresa..."
            />
            <Search className="absolute right-3 top-2.5 sm:top-3 w-4 h-4 text-gray-400" />
          </div>
          
          {/* Resultados de búsqueda de clientes */}
          {showCustomerResults && (
            <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg shadow-lg mt-1 max-h-60 overflow-y-auto overflow-x-hidden">
              {customers.map((customer, index) => (
                <div
                  key={customer.id}
                  className="px-3 sm:px-4 py-2 sm:py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                  onClick={() => handleCustomerSelect(customer)}
                >
                  <div className="font-medium text-gray-900 text-sm sm:text-base">
                    {customer.company ? customer.company : customer.name}
                  </div>
                  {customer.company && customer.name && (
                    <div className="text-xs sm:text-sm text-gray-600">Contacto: {customer.name}</div>
                  )}
                  {customer.company && !customer.name && (
                    <div className="text-xs sm:text-sm text-gray-600">Empresa</div>
                  )}
                  {!customer.company && customer.name && (
                    <div className="text-xs sm:text-sm text-gray-600">Cliente</div>
                  )}
                  {customer.ruc && (
                    <div className="text-xs sm:text-sm text-gray-500">RUC: {customer.ruc}</div>
                  )}
                  {customer.email && (
                    <div className="text-xs sm:text-sm text-gray-500">{customer.email}</div>
                  )}
                </div>
              ))}
              {customers.length === 0 && !formData.customer_id && (
                <div className="px-3 sm:px-4 py-2 sm:py-3 text-gray-500 text-sm">
                  No se encontraron clientes
                </div>
              )}
            </div>
          )}
          
          {errors.customer_name && (
            <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.customer_name}</p>
          )}
        </div>

        {/* Número de orden (manual) */}
        <div>
          <label htmlFor="order_number" className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
            Número de Orden de Pedido *
          </label>
          <input
            type="text"
            id="order_number"
            name="order_number"
            value={formData.order_number || ''}
            onChange={handleInputChange}
            className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base ${
              errors.order_number ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Ingrese el número de orden proporcionado por el cliente"
          />
          {errors.order_number && (
            <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.order_number}</p>
          )}
        </div>

        {/* Productos agregados a la orden */}
        <div className="mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 sm:mb-4 space-y-2 sm:space-y-0">
            <h4 className="text-base sm:text-lg font-semibold text-gray-900">Productos en la Orden</h4>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addOrderItem}
              className="flex items-center self-start sm:self-auto"
            >
              <Package className="w-4 h-4 mr-2" />
              <span className="text-xs sm:text-sm">Agregar Producto</span>
            </Button>
          </div>
          
          {errors.items && (
            <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.items}</p>
          )}
          
          {/* Tabla de productos - Versión móvil optimizada */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            {/* Vista de tabla para pantallas medianas y grandes */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Producto</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cantidad</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Precio Unitario</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subtotal</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.items.map((item, index) => (
                    <tr key={index} className="border-b border-gray-100">
                      <td className="px-4 py-3 text-sm">
                        <select
                          value={item.product_id}
                          onChange={(e) => updateOrderItem(index, 'product_id', parseInt(e.target.value))}
                          className={`w-full px-2 py-1 border rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                            errors[`product_id_${index}`] ? 'border-red-500' : 'border-gray-300'
                          }`}
                        >
                          <option value="0">Seleccione producto</option>
                          {products.map(product => (
                            <option key={product.id} value={product.id}>
                              {product.name} - {product.category}
                            </option>
                          ))}
                        </select>
                        {errors[`product_id_${index}`] && (
                          <p className="mt-1 text-xs text-red-600">{errors[`product_id_${index}`]}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateOrderItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                          min="1"
                          className={`w-full px-2 py-1 border rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                            errors[`quantity_${index}`] ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {errors[`quantity_${index}`] && (
                          <p className="mt-1 text-xs text-red-600">{errors[`quantity_${index}`]}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                            {getCurrencySymbol()}
                          </span>
                          <input
                            type="number"
                            value={item.unit_price}
                            onChange={(e) => updateOrderItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                            step="0.01"
                            min="0"
                            className={`w-full pl-6 pr-2 py-1 border rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                              errors[`unit_price_${index}`] ? 'border-red-500' : 'border-gray-300'
                            }`}
                          />
                        </div>
                        {errors[`unit_price_${index}`] && (
                          <p className="mt-1 text-xs text-red-600">{errors[`unit_price_${index}`]}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium">
                        {formatCurrency(item.subtotal)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeOrderItem(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Vista de tarjetas para móviles */}
            <div className="sm:hidden">
              {formData.items.map((item, index) => (
                <div key={index} className="p-3 border-b border-gray-100 last:border-b-0">
                  <div className="flex justify-between items-start mb-2">
                    <h5 className="text-sm font-medium text-gray-900">Producto #{index + 1}</h5>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeOrderItem(index)}
                      className="text-red-600 hover:text-red-800 p-1"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Producto</label>
                      <select
                        value={item.product_id}
                        onChange={(e) => updateOrderItem(index, 'product_id', parseInt(e.target.value))}
                        className={`w-full px-2 py-1.5 border rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                          errors[`product_id_${index}`] ? 'border-red-500' : 'border-gray-300'
                        }`}
                      >
                        <option value="0">Seleccione producto</option>
                        {products.map(product => (
                          <option key={product.id} value={product.id}>
                            {product.name}
                          </option>
                        ))}
                      </select>
                      {errors[`product_id_${index}`] && (
                        <p className="mt-1 text-xs text-red-600">{errors[`product_id_${index}`]}</p>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Cantidad</label>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateOrderItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                          min="1"
                          className={`w-full px-2 py-1.5 border rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                            errors[`quantity_${index}`] ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {errors[`quantity_${index}`] && (
                          <p className="mt-1 text-xs text-red-600">{errors[`quantity_${index}`]}</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Precio Unit.</label>
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs">
                            {getCurrencySymbol()}
                          </span>
                          <input
                            type="number"
                            value={item.unit_price}
                            onChange={(e) => updateOrderItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                            step="0.01"
                            min="0"
                            className={`w-full pl-5 pr-2 py-1.5 border rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                              errors[`unit_price_${index}`] ? 'border-red-500' : 'border-gray-300'
                            }`}
                          />
                        </div>
                        {errors[`unit_price_${index}`] && (
                          <p className="mt-1 text-xs text-red-600">{errors[`unit_price_${index}`]}</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="pt-1 border-t border-gray-100">
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-medium text-gray-700">Subtotal:</span>
                        <span className="text-sm font-bold text-gray-900">{formatCurrency(item.subtotal)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 lg:gap-6">
          {/* Moneda */}
          <div>
            <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              Moneda *
            </label>
            <select
              id="currency"
              name="currency"
              value={formData.currency}
              onChange={handleInputChange}
              className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base ${
                errors.currency ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="PEN">Soles (PEN)</option>
              <option value="USD" selected>Dólares (USD)</option>
            </select>
            {errors.currency && (
              <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.currency}</p>
            )}
          </div>

          {/* Fecha de orden */}
          <div>
            <label htmlFor="order_date" className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              Fecha de Orden *
            </label>
            <input
              type="date"
              id="order_date"
              name="order_date"
              value={formData.order_date}
              onChange={handleInputChange}
              className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base ${
                errors.order_date ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.order_date && (
              <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.order_date}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 lg:gap-6">
          {/* Condición de Pago */}
          <div>
            <label htmlFor="payment_type" className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              Condición de Pago *
            </label>
            <select
              id="payment_type"
              name="payment_type"
              value={formData.payment_type}
              onChange={(e) => {
                const paymentType = e.target.value as 'contado' | 'credito';
                const creditDays = paymentType === 'contado' ? 0 : formData.credit_days || 30;
                const paymentTerms = paymentType === 'contado' ? 'Contado' : `Crédito a ${creditDays} días`;
                
                setFormData(prev => ({
                  ...prev,
                  payment_type: paymentType,
                  credit_days: creditDays,
                  payment_terms: paymentTerms
                }));
                
                // Limpiar error de payment_terms cuando se selecciona una opción
                if (errors.payment_terms) {
                  setErrors(prev => ({ ...prev, payment_terms: '' }));
                }
              }}
              className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base ${
                errors.payment_terms ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="contado">Contado</option>
              <option value="credito">Crédito</option>
            </select>
            {errors.payment_terms && (
              <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.payment_terms}</p>
            )}
          </div>

          {/* Días de Crédito (solo visible cuando es crédito) */}
          {formData.payment_type === 'credito' && (
            <div>
              <label htmlFor="credit_days" className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Días de Crédito *
              </label>
              <input
                type="number"
                id="credit_days"
                name="credit_days"
                min="1"
                max="365"
                value={formData.credit_days}
                onChange={(e) => {
                  const creditDays = parseInt(e.target.value) || 30;
                  const paymentTerms = `Crédito a ${creditDays} días`;
                  
                  setFormData(prev => ({
                    ...prev,
                    credit_days: creditDays,
                    payment_terms: paymentTerms
                  }));
                }}
                className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base ${
                  errors.payment_terms ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.payment_terms && (
                <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.payment_terms}</p>
              )}
            </div>
          )}

          {/* Tiempo de entrega */}
          <div>
            <label htmlFor="delivery_days" className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              Tiempo de Entrega (días hábiles) *
            </label>
            <input
              type="number"
              id="delivery_days"
              name="delivery_days"
              min="1"
              max="365"
              value={formData.delivery_days}
              onChange={(e) => {
                const deliveryDays = parseInt(e.target.value) || 5;
                const deliveryTime = `${deliveryDays} días hábiles después de la orden de compra`;
                
                setFormData(prev => ({
                  ...prev,
                  delivery_days: deliveryDays,
                  delivery_time: deliveryTime
                }));
              }}
              className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base ${
                errors.delivery_days ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.delivery_days && (
              <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.delivery_days}</p>
            )}
          </div>
        </div>

        {/* Cálculo de Detracciones */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4">
          <div className="flex items-center mb-2 sm:mb-3">
            <Percent className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 mr-2" />
            <h4 className="font-medium text-gray-900 text-sm sm:text-base">Cálculo de Detracciones</h4>
          </div>
          <div className="space-y-2 sm:space-y-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="calculate_detraccion"
                name="calculate_detraccion"
                checked={formData.calculate_detraccion}
                onChange={handleInputChange}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <label htmlFor="calculate_detraccion" className="ml-2 text-sm text-gray-700">
                Activar cálculo de detracciones
              </label>
            </div>
            
            {formData.calculate_detraccion && (
              <div>
                <label htmlFor="detraccion_percentage" className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Porcentaje de Detracción (%)
                </label>
                <input
                  type="number"
                  id="detraccion_percentage"
                  name="detraccion_percentage"
                  value={formData.detraccion_percentage}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  max="100"
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base"
                />
              </div>
            )}
          </div>
        </div>

        {/* Resumen de montos calculados */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4">
          <div className="flex items-center mb-2 sm:mb-3">
            <Calculator className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 mr-2" />
            <h4 className="font-medium text-gray-900 text-sm sm:text-base">Resumen de Montos</h4>
          </div>
          <div className="space-y-2 sm:space-y-3">
            <div className="flex justify-between">
              <span className="text-xs sm:text-sm text-gray-600">Subtotal sin IGV:</span>
              <span className="text-xs sm:text-sm font-medium">{formatCurrency(calculatedAmounts.subtotal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs sm:text-sm text-gray-600">IGV (18%):</span>
              <span className="text-xs sm:text-sm font-medium">{formatCurrency(calculatedAmounts.igvAmount)}</span>
            </div>
            <div className="border-t pt-2 sm:pt-3">
              <div className="flex justify-between">
                <span className="text-xs sm:text-sm text-gray-900 font-medium">Total con IGV:</span>
                <span className="text-sm sm:text-lg font-bold">{formatCurrency(calculatedAmounts.totalWithIgv)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Información de detracciones (informativo) */}
        {formData.calculate_detraccion && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
            <div className="flex items-center mb-2 sm:mb-3">
              <Percent className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mr-2" />
              <h4 className="font-medium text-blue-900 text-sm sm:text-base">Información de Detracciones</h4>
            </div>
            <div className="space-y-1 sm:space-y-2">
              <div className="flex justify-between">
                <span className="text-xs sm:text-sm text-blue-700">Porcentaje de detracción:</span>
                <span className="text-xs sm:text-sm font-medium text-blue-900">{formData.detraccion_percentage}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs sm:text-sm text-blue-700">Monto de detracción:</span>
                <span className="text-xs sm:text-sm font-medium text-blue-900">{formatCurrency(calculatedAmounts.detraccionAmount)}</span>
              </div>
              <div className="bg-blue-100 p-2 sm:p-3 rounded-lg mt-1 sm:mt-2">
                <div className="flex justify-between">
                  <span className="text-xs sm:text-sm text-blue-900 font-medium">Monto neto a cobrar:</span>
                  <span className="text-sm sm:text-lg font-bold text-blue-900">
                    {formatCurrency(calculatedAmounts.totalWithIgv - calculatedAmounts.detraccionAmount)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Documento de la orden */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
            Documento de Orden de Pedido (Opcional)
          </label>
          
          {/* Mostrar documento existente si se está editando */}
          {order && order.order_document_url && (
            <div className="mb-3 p-2 sm:p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
                <div className="flex items-center space-x-2">
                  <FileText className="w-4 h-4 text-gray-600" />
                  <span className="text-xs sm:text-sm font-medium text-gray-700">Documento actual:</span>
                  <span className="text-xs sm:text-sm text-gray-600 truncate">{order.order_document}</span>
                </div>
                <a
                  href={order.order_document_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary-600 hover:text-primary-800 underline"
                >
                  Ver documento
                </a>
              </div>
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
            <label className="cursor-pointer w-full sm:w-auto">
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={handleDocumentUpload}
                disabled={isUploading}
                className="hidden"
              />
              <div className={`flex items-center justify-center px-3 sm:px-4 py-2 sm:py-2.5 border-2 border-dashed rounded-lg transition-colors w-full sm:w-auto ${
                isUploading
                  ? 'border-gray-300 bg-gray-50 cursor-not-allowed'
                  : 'border-primary-300 hover:border-primary-500 bg-primary-50 cursor-pointer'
              }`}>
                {isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    <span className="text-sm">Procesando...</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    <span className="text-sm">Seleccionar documento</span>
                  </>
                )}
              </div>
            </label>
            
            {formData.order_document && (
              <div className="flex items-center text-xs sm:text-sm text-gray-600">
                <span className="truncate max-w-[150px] sm:max-w-[200px]">
                  {formData.order_document instanceof File
                    ? formData.order_document.name
                    : 'Documento seleccionado'}
                </span>
              </div>
            )}
          </div>
          <div className="mt-2 space-y-1">
            <p className="text-xs text-gray-500">
              Formatos: JPG, PNG, GIF, PDF. Máximo 10MB
            </p>
            <p className="text-xs text-blue-600">
              💡 El documento se subirá automáticamente cuando guarde la orden
            </p>
          </div>
        </div>

        {/* Notas */}
        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
            Notas respecto al pedido (Opcional)
          </label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            rows={3}
            className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base resize-none"
            placeholder="Notas adicionales respecto al pedido..."
          />
        </div>

        {/* Botones */}
        <div className="flex flex-col sm:flex-row sm:justify-end sm:space-x-4 space-y-2 sm:space-y-0 pt-3 sm:pt-6 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="w-full sm:w-auto py-2 sm:py-2.5 text-sm sm:text-base"
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            className="btn-primary w-full sm:w-auto py-2 sm:py-2.5 text-sm sm:text-base"
            disabled={isLoading || isUploading || isLoadingQuote}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {order ? 'Actualizar Orden' : 'Registrar Orden'}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default OrderForm;